/**
 * useAuth — Zustand-backed authentication + business membership state.
 *
 * Responsibility:
 *   - Persist JWT in sessionStorage (apiClient writes Authorization header).
 *   - Persist active branch ID in localStorage (bizClient writes
 *     `x-business-branch-id` header on every /api/biz/* call).
 *   - Hold the actor's full membership graph so TenantProvider /
 *     TenantSwitcher can render the branch picker without round-tripping.
 *   - Expose `login`, `logout`, `bootstrap`, `setActiveBranch`,
 *     `refreshMemberships`.
 *
 * Two-step authentication flow (P1d Real Cashier Wire):
 *   1. POST /api/v1/auth/login                  -> access+refresh token + actor
 *   2. GET  /api/biz/auth/me                    -> memberships + defaultBranchId
 *
 * Step (2) is the gate that distinguishes a *business operator* from a
 * *consumer* using the same actor namespace. A consumer who somehow
 * lands on /login here will succeed in step (1) and fail in step (2)
 * with `ACTOR_NOT_BUSINESS_STAFF` (403). The login page maps that into
 * a friendly error and clears any leaked token.
 *
 * Bootstrap (page refresh) is fire-and-forget from `main.tsx`; it
 * silently logs the user out when /api/biz/auth/me 401/403/404s — this
 * is the path that handles "token expired", "actor archived", or
 * "operator role revoked" without ever exposing a corrupt session.
 *
 * @see web-business/src/shared/api/apiClient.ts
 * @see web-business/src/shared/api/bizClient.ts
 * @see backend/src/api/business/biz-auth-routes.ts
 * @see web-business/src/shared/context/TenantContext.tsx
 */
import { create } from 'zustand';

import {
  ApiError,
  apiPost,
  getActiveBranchId,
  getStoredAuthToken,
  setActiveBranchId,
  setStoredAuthToken,
} from '../api/apiClient';
import { bizGet } from '../api/bizClient';

// ---------------------------------------------------------------------------
// Types — mirror backend BusinessAuthMeResponse projections
// ---------------------------------------------------------------------------

export type BusinessRoleLabel =
  | 'owner'
  | 'manager'
  | 'cashier'
  | 'reception'
  | 'groomer'
  | 'vet'
  | 'tech'
  | 'trainer'
  | 'walker'
  | 'driver'
  | 'sales_associate'
  | 'platform_admin';

export interface BusinessVerticalCapability {
  id: string;
  subVerticalKey: string;
  profileVersion: string | null;
}

export interface BusinessMembership {
  staffId: string;
  role: BusinessRoleLabel;
  status: string;
  startedAt: string;
  branchId: string;
  branchSlug: string;
  branchName: string;
  branchTimezone: string;
  legalEntityId: string;
  legalEntityName: string;
  currency: string;
  countryCode: string;
  organizationId: string | null;
  organizationName: string | null;
  organizationSlug: string | null;
  verticalCapabilities: BusinessVerticalCapability[];
  primaryVerticalCapabilityId: string | null;
}

/**
 * Backend projection of the actor returned by `/api/biz/auth/me`.
 * Treat as raw — `AuthUser` adds the fields the UI actually consumes.
 */
export interface BusinessActorPayload {
  id: string;
  fullName: string;
  actorType: string;
  avatarUrl: string | null;
  lastLoginAt: string | null;
}

/**
 * UI-facing authenticated user. Extends the backend projection with three
 * derived fields kept for backwards compatibility with the existing layouts
 * and feature hooks (AdminLayout, BusinessLayout, useBusinessProfiles, …):
 *   - displayName: alias of `fullName` (UI components consume this name).
 *   - email: populated only by the login flow (step 1 returns it). Bootstrap
 *     leaves it null because `/api/biz/auth/me` does NOT decrypt PII; mock
 *     JWT generators downstream tolerate null and never round-trip it.
 *   - platformRole: 'platform_admin' if any membership carries that role,
 *     otherwise null. Used as a single-bit gate by AdminLayout.
 */
/**
 * Legacy platform-role gate. Live `/biz/auth/me` does not return one — we
 * derive 'platform_admin' from the membership graph (any membership with
 * that role) and leave `null` otherwise. The 'business_owner' value is
 * carried for the mock onboarding flow only and is not produced by the
 * real backend.
 */
export type LegacyPlatformRole = 'platform_admin' | 'business_owner' | null;

export interface AuthUser extends BusinessActorPayload {
  displayName: string;
  email: string | null;
  platformRole: LegacyPlatformRole;
}

interface BusinessAuthMeResponseBE {
  actor: BusinessActorPayload;
  memberships: BusinessMembership[];
  defaultBranchId: string | null;
  resolvedAt: string;
}

interface ActorLoginResponseBE {
  user: {
    id: string;
    email: string;
    actorType: string;
    fullName: string;
    createdAt: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  memberships: BusinessMembership[];
  activeBranchId: string | null;
  isResolving: boolean;
  isBootstrapping: boolean;
  /** Last error surfaced by bootstrap — UI may surface it as a banner. */
  bootstrapError: string | null;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => void;
  bootstrap: () => Promise<void>;
  setActiveBranch: (branchId: string) => void;
  refreshMemberships: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

function clearLocalSession(): void {
  setStoredAuthToken(null);
  setActiveBranchId(null);
}

function pickActiveBranch(
  memberships: BusinessMembership[],
  defaultBranchId: string | null,
  current: string | null,
): string | null {
  if (current && memberships.some((m) => m.branchId === current)) {
    return current;
  }
  if (defaultBranchId && memberships.some((m) => m.branchId === defaultBranchId)) {
    return defaultBranchId;
  }
  return memberships[0]?.branchId ?? null;
}

function derivePlatformRole(
  memberships: BusinessMembership[],
): LegacyPlatformRole {
  return memberships.some((m) => m.role === 'platform_admin')
    ? 'platform_admin'
    : null;
}

function buildAuthUser(
  actor: BusinessActorPayload,
  memberships: BusinessMembership[],
  email: string | null,
): AuthUser {
  return {
    ...actor,
    displayName: actor.fullName,
    email,
    platformRole: derivePlatformRole(memberships),
  };
}

export const useAuth = create<AuthState>((set, get) => ({
  token: getStoredAuthToken(),
  user: null,
  memberships: [],
  activeBranchId: getActiveBranchId(),
  isResolving: false,
  isBootstrapping: false,
  bootstrapError: null,

  login: async (email, password) => {
    set({ isResolving: true });
    try {
      // Step 1 — actor authentication (shared with consumer surface).
      const loginRes = await apiPost<ActorLoginResponseBE>('/auth/login', {
        email,
        password,
      });
      setStoredAuthToken(loginRes.tokens.accessToken);

      // Step 2 — business membership lookup. This is the operator gate;
      // it 403s for consumer-only actors.
      let me: BusinessAuthMeResponseBE;
      try {
        me = await bizGet<BusinessAuthMeResponseBE>('/biz/auth/me');
      } catch (err) {
        // Tear down the partial session before re-throwing so the next
        // call is not contaminated by an orphaned access token.
        clearLocalSession();
        set({
          token: null,
          user: null,
          memberships: [],
          activeBranchId: null,
          isResolving: false,
        });
        throw err;
      }

      const activeBranchId = pickActiveBranch(
        me.memberships,
        me.defaultBranchId,
        getActiveBranchId(),
      );
      setActiveBranchId(activeBranchId);

      const user = buildAuthUser(me.actor, me.memberships, loginRes.user.email);

      set({
        token: loginRes.tokens.accessToken,
        user,
        memberships: me.memberships,
        activeBranchId,
        isResolving: false,
        bootstrapError: null,
      });
      return user;
    } catch (err) {
      clearLocalSession();
      set({
        token: null,
        user: null,
        memberships: [],
        activeBranchId: null,
        isResolving: false,
      });
      throw err;
    }
  },

  logout: () => {
    clearLocalSession();
    set({
      token: null,
      user: null,
      memberships: [],
      activeBranchId: null,
      bootstrapError: null,
    });
  },

  bootstrap: async () => {
    const token = getStoredAuthToken();
    if (!token) {
      set({ token: null, isBootstrapping: false });
      return;
    }
    set({ token, isBootstrapping: true, bootstrapError: null });
    try {
      const me = await bizGet<BusinessAuthMeResponseBE>('/biz/auth/me');
      const activeBranchId = pickActiveBranch(
        me.memberships,
        me.defaultBranchId,
        getActiveBranchId(),
      );
      setActiveBranchId(activeBranchId);
      // Bootstrap path has no access to the plaintext email (the
      // /me endpoint deliberately omits PII). Preserve any prior value
      // from this session (e.g. login → reload) but otherwise leave null.
      const priorEmail = get().user?.email ?? null;
      const user = buildAuthUser(me.actor, me.memberships, priorEmail);
      set({
        token,
        user,
        memberships: me.memberships,
        activeBranchId,
        isBootstrapping: false,
        bootstrapError: null,
      });
    } catch (err) {
      // Token expired / actor archived / operator role revoked — wipe
      // everything so ProtectedRoute redirects to /login. We deliberately
      // surface only a generic message; the LoginPage will show the
      // canonical "session expired" copy on the next attempt.
      clearLocalSession();
      const message =
        err instanceof ApiError
          ? err.payload.detail || err.payload.title
          : err instanceof Error
            ? err.message
            : 'Bootstrap failed';
      set({
        token: null,
        user: null,
        memberships: [],
        activeBranchId: null,
        isBootstrapping: false,
        bootstrapError: message,
      });
    }
  },

  setActiveBranch: (branchId) => {
    const { memberships } = get();
    if (!memberships.some((m) => m.branchId === branchId)) {
      throw new Error(`Branch ${branchId} is not in user's memberships`);
    }
    setActiveBranchId(branchId);
    set({ activeBranchId: branchId });
  },

  refreshMemberships: async () => {
    const token = getStoredAuthToken();
    if (!token) return;
    try {
      const me = await bizGet<BusinessAuthMeResponseBE>('/biz/auth/me');
      const activeBranchId = pickActiveBranch(
        me.memberships,
        me.defaultBranchId,
        getActiveBranchId(),
      );
      setActiveBranchId(activeBranchId);
      const priorEmail = get().user?.email ?? null;
      const user = buildAuthUser(me.actor, me.memberships, priorEmail);
      set({
        user,
        memberships: me.memberships,
        activeBranchId,
      });
    } catch {
      // Silent: caller observes via memberships array. bootstrap() owns
      // the unauthenticated cleanup path.
    }
  },
}));

export function useIsAuthenticated(): boolean {
  return useAuth((s) => Boolean(s.token && s.user));
}

export function useActiveMembership(): BusinessMembership | null {
  return useAuth((s) => {
    if (!s.activeBranchId) return s.memberships[0] ?? null;
    return (
      s.memberships.find((m) => m.branchId === s.activeBranchId) ??
      s.memberships[0] ??
      null
    );
  });
}
